import React, { useState, useEffect } from 'react';
import { formatNumber, formatCredits, formatCurrency } from '../../utils/number';
import { isAITransformation, getTransformationBadgeClass } from '../../utils/transformations';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { productService } from '../../services/product';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  Sparkles,
  Move,
  FileImage,
  Zap,
  Palette,
  Plus,
  X,
  Loader2,
  Calculator,
  CheckCircle,
  Settings,
  Clock,
  DollarSign,
  Info,
  Wand2,
  Eye,
  Tag,
  Brain,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  Crown,
  ArrowUp,
  Shirt,
  Home
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Transformation, TransformationPreset, jobService } from '../../services/job';
import { storeService } from '../../services/store';
import { subscriptionService, CurrentSubscription } from '../../services/subscription';
import { WarningBanner, CreditLimitBanner } from '../ui/warning-banner';

interface TransformationPanelProps {
  selectedProductIds: string[];
  storeId: string;
  imageCount: number;
  onJobsCreated: (identifier: string, isAsync?: boolean, metadata?: {
    storeId: string;
    storeName: string;
    productCount: number;
  }) => void;
  onCancel: () => void;
}

export function TransformationPanel({ 
  selectedProductIds, 
  storeId, 
  imageCount: initialImageCount, 
  onJobsCreated, 
  onCancel 
}: TransformationPanelProps) {
  const { hasPermission } = useOrganization();
  const [transformations, setTransformations] = useState<Transformation[]>([]);
  const [presets, setPresets] = useState<Record<string, TransformationPreset>>({});
  const [userPlan, setUserPlan] = useState<string>('Silver');
  const [isCreatingJobs, setIsCreatingJobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTransformation, setCustomTransformation] = useState<Partial<Transformation>>({});
  const [actualImageCount, setActualImageCount] = useState(initialImageCount);
  const [actualMainImageCount, setActualMainImageCount] = useState(0);
  const [actualGalleryImageCount, setActualGalleryImageCount] = useState(0);
  const [isLoadingImageCount, setIsLoadingImageCount] = useState(false);
  const [storeName, setStoreName] = useState<string>('Unknown Store');
  const [processingInfo, setProcessingInfo] = useState<any>(null);
  const [isAnalyzingTransformations, setIsAnalyzingTransformations] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [imageTypeSelection, setImageTypeSelection] = useState<'all' | 'main' | 'gallery'>('all');

  // Calculate effective image count based on image type selection
  const effectiveImageCount = React.useMemo(() => {
    if (isLoadingImageCount) return 0;

    if (imageTypeSelection === 'main') {
      return actualMainImageCount; // Use actual from backend
    } else if (imageTypeSelection === 'gallery') {
      return actualGalleryImageCount; // Use actual from backend
    }
    return actualImageCount; // All images
  }, [imageTypeSelection, actualImageCount, actualMainImageCount, actualGalleryImageCount, isLoadingImageCount]);

  useEffect(() => {
    loadPresets();
    loadStoreName();
    loadCurrentSubscription();
    if (initialImageCount === 0 && selectedProductIds.length > 0) {
      calculateImageCount();
    }
  }, [storeId]);

  // Analyze transformations when they change or when image count changes
  useEffect(() => {
    if (transformations.length > 0) {
      analyzeTransformations();
    } else {
      setProcessingInfo(null);
    }
  }, [transformations, effectiveImageCount]);

  const loadStoreName = async () => {
    try {
      const store = await storeService.getStore(storeId);
      if (store && store.name) {
        setStoreName(store.name);
      }
    } catch (error) {
      console.error('Failed to load store name:', error);
      setStoreName('Unknown Store');
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      setIsLoadingSubscription(true);
      const subscription = await subscriptionService.getCurrentSubscription();
      setCurrentSubscription(subscription);
    } catch (error) {
      console.error('Failed to load subscription:', error);
      setCurrentSubscription(null);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const calculateImageCount = async () => {
    setIsLoadingImageCount(true);
    try {
      // Get actual image counts from the backend
      const imageCountData = await productService.getImageCounts(selectedProductIds);

      // Use the actual counts from backend
      setActualImageCount(imageCountData.total_images);
      setActualMainImageCount(imageCountData.total_main_images || 0);
      setActualGalleryImageCount(imageCountData.total_gallery_images || 0);
    } catch (error) {
      console.error('Failed to calculate image count:', error);
      // Fallback to estimation (3 images per product)
      setActualImageCount(selectedProductIds.length * 3);
      setActualMainImageCount(selectedProductIds.length);
      setActualGalleryImageCount(selectedProductIds.length * 2);
    } finally {
      setIsLoadingImageCount(false);
    }
  };

  const loadPresets = async () => {
    try {
      const response = await jobService.getTransformationPresets();
      setPresets(response.data);
      if (response.user_plan) {
        setUserPlan(response.user_plan);
      }
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const analyzeTransformations = async () => {
    if (transformations.length === 0) return;
    
    setIsAnalyzingTransformations(true);
    try {
      const response = await fetch('/api/jobs/analyze-transformations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ transformations })
      });

      if (response.ok) {
        const result = await response.json();
        setProcessingInfo(result.data);
      }
    } catch (error) {
      console.error('Failed to analyze transformations:', error);
    } finally {
      setIsAnalyzingTransformations(false);
    }
  };

  const getTransformationIcon = (type: string) => {
    switch (type) {
      case 'background_removal': return <Sparkles className="w-4 h-4" />;
      case 'resize': return <Move className="w-4 h-4" />;
      case 'format_conversion': return <FileImage className="w-4 h-4" />;
      case 'ai_upscale': return <Zap className="w-4 h-4" />;
      case 'color_adjustment': return <Palette className="w-4 h-4" />;
      case 'generative_fill': return <Wand2 className="w-4 h-4" />;
      case 'generative_remove': return <Eye className="w-4 h-4" />;
      case 'generative_replace': return <Brain className="w-4 h-4" />;
      case 'generative_background_replace': return <Brain className="w-4 h-4" />;
      case 'auto_tagging': return <Tag className="w-4 h-4" />;
      case 'object_detection': return <Eye className="w-4 h-4" />;
      case 'invisible_mannequin': return <Shirt className="w-4 h-4" />;
      case 'object_placement': return <Home className="w-4 h-4" />;
      default: return <Settings className="w-4 h-4" />;
    }
  };

  const addPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (preset) {
      setTransformations(preset.transformations);
      setError(null);
    }
  };

  const addCustomTransformation = () => {
    if (!customTransformation.type) {
      setError('Please select a transformation type');
      return;
    }

    // Validate based on type
    if (customTransformation.type === 'resize') {
      if (!customTransformation.width || !customTransformation.height) {
        setError('Width and height are required for resize transformation');
        return;
      }
      if (customTransformation.width > 4000 || customTransformation.height > 4000) {
        setError('Maximum dimensions are 4000x4000 pixels');
        return;
      }
    }

    if (customTransformation.type === 'format_conversion' && !customTransformation.format) {
      setError('Format is required for format conversion');
      return;
    }
    
    // AI transformations validation
    if (customTransformation.type === 'generative_fill') {
      if (!customTransformation.width || !customTransformation.height) {
        setError('Width and height are required for generative fill');
        return;
      }
      // Set default prompt if not selected
      if (!customTransformation.prompt) {
        customTransformation.prompt = 'auto';
      }
    }

    if (customTransformation.type === 'generative_remove' && !customTransformation.prompt) {
      setError('Prompt is required for generative remove');
      return;
    }

    if (customTransformation.type === 'generative_replace') {
      if (!customTransformation.from || !customTransformation.to) {
        setError('Both "from" and "to" descriptions are required for generative replace');
        return;
      }
    }

    if (customTransformation.type === 'generative_background_replace' && !customTransformation.prompt) {
      setError('Background description is required for generative background replace');
      return;
    }

    if (customTransformation.type === 'ai_upscale' && !customTransformation.scale) {
      setError('Scale factor is required for AI upscale');
      return;
    }

    if (customTransformation.type === 'color_adjustment') {
      const hasAnyAdjustment = ['brightness', 'saturation', 'contrast', 'hue', 'vibrance'].some(
        prop => customTransformation[prop as keyof Transformation] !== undefined
      );
      if (!hasAnyAdjustment) {
        setError('At least one color adjustment parameter is required');
        return;
      }
    }

    setTransformations([...transformations, customTransformation as Transformation]);
    setCustomTransformation({});
    setShowCustomForm(false);
    setError(null);
  };

  const removeTransformation = (index: number) => {
    setTransformations(transformations.filter((_, i) => i !== index));
  };

  const moveTransformation = (fromIndex: number, toIndex: number) => {
    const newTransformations = [...transformations];
    const [movedItem] = newTransformations.splice(fromIndex, 1);
    newTransformations.splice(toIndex, 0, movedItem);
    setTransformations(newTransformations);
  };

  const getOrderPriority = (type: string) => {
    // Centralized transformation order - matches backend and Python service
    const orderPriority = {
      // Step 1: Content modification
      'generative_remove': 1,
      'generative_replace': 2,
      
      // Step 2: Background operations
      'background_removal': 3,
      'generative_background_replace': 4,
      
      // Step 3: Canvas/size operations
      'generative_fill': 5,
      'ai_upscale': 6,
      'resize': 7,
      'crop': 8,
      'rotate': 9,
      
      // Step 4: Visual adjustments
      'quality_enhancement': 10,
      'auto_enhancement': 11,
      'color_adjustment': 12,
      'sharpen': 13,
      'blur': 14,
      'noise_reduction': 15,
      
      // Step 5: Format and output
      'format_conversion': 16,
      
      // Step 6: Analysis
      'auto_tagging': 17,
      'object_detection': 18
    };
    return orderPriority[type as keyof typeof orderPriority] || 99;
  };

  const autoSortTransformations = () => {
    const sortedTransformations = [...transformations].sort((a, b) => {
      const priorityA = getOrderPriority(a.type);
      const priorityB = getOrderPriority(b.type);
      return priorityA - priorityB;
    });

    setTransformations(sortedTransformations);
  };

  const isOptimalOrder = () => {
    for (let i = 0; i < transformations.length - 1; i++) {
      const currentPriority = getOrderPriority(transformations[i].type);
      const nextPriority = getOrderPriority(transformations[i + 1].type);
      if (currentPriority > nextPriority) {
        return false;
      }
    }
    return true;
  };

  const createJobs = async () => {
    if (transformations.length === 0) {
      setError('Please add at least one transformation');
      return;
    }

    setIsCreatingJobs(true);
    setError(null);

    try {
      const response = await jobService.createBatchJobs({
        product_ids: selectedProductIds,
        transformations,
        store_id: storeId,
        image_type: imageTypeSelection // Add image type selection
      });

      if (response.success) {
        // Handle both sync (batch_id) and async (job_id) responses
        const identifier = response.data.batch_id || response.data.job_id || 'unknown';
        const isAsync = response.data.async || false;
        
        // Prepare metadata for history tracking
        const metadata = {
          storeId,
          storeName,
          productCount: selectedProductIds.length
        };
        
        onJobsCreated(identifier, isAsync, metadata);
      } else {
        console.error('Job creation failed:', response);
        setError(response.message || 'Failed to create jobs');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create processing jobs');
    } finally {
      setIsCreatingJobs(false);
    }
  };

  const estimatedCost = jobService.calculateEstimatedCost(transformations, effectiveImageCount);

  // Check if current transformations would exceed credit limit
  const checkCreditLimit = () => {
    if (!currentSubscription || transformations.length === 0 || isLoadingSubscription) {
      return { isOverLimit: false, details: null };
    }

    const requiredCredits = Math.ceil(estimatedCost);
    const remainingCredits = currentSubscription.usage_limit - currentSubscription.current_usage;
    const isOverLimit = requiredCredits > remainingCredits;

    return {
      isOverLimit,
      details: {
        required: requiredCredits,
        remaining: remainingCredits,
        current: currentSubscription.current_usage,
        limit: currentSubscription.usage_limit,
        planName: currentSubscription.plan_name,
        products: selectedProductIds.length,
        images: effectiveImageCount,
        creditsPerImage: transformations.length > 0 && effectiveImageCount > 0 ? estimatedCost / effectiveImageCount : 0
      }
    };
  };

  const creditCheck = checkCreditLimit();

  // Calculate usage information for expensive transformations
  const getUsageInfo = (preset: TransformationPreset) => {
    if (!preset.usageInfo || !currentSubscription || isLoadingSubscription) {
      return null;
    }

    const remainingCredits = currentSubscription.usage_limit - currentSubscription.current_usage;
    const creditsPerTransformation = preset.estimatedCredits || 0;
    const maxTransformations = Math.floor(remainingCredits / creditsPerTransformation);
    
    return {
      remainingCredits,
      creditsPerTransformation,
      maxTransformations,
      costPerTransformation: (creditsPerTransformation * 0.01).toFixed(2)
    };
  };

  const renderProcessingMethodBadge = () => {
    if (!processingInfo) return null;

    const isAiProcessing = processingInfo.processing_info?.method === 'upload';
    
    return (
      <Badge 
        variant={isAiProcessing ? 'default' : 'secondary'}
        className={isAiProcessing 
          ? 'text-white bg-gradient-to-r from-blue-500 to-purple-500' 
          : 'text-muted-foreground bg-muted'
        }
      >
        {isAiProcessing ? (
          <>
            <Sparkles className="mr-1 w-3 h-3" />
            AI Processing
          </>
        ) : (
          <>
            <Zap className="mr-1 w-3 h-3" />
            Basic Processing
          </>
        )}
      </Badge>
    );
  };

  const renderProcessingEstimate = () => {
    if (!processingInfo) return null;

    const stats = processingInfo.stats;
    const totalCredits = stats?.estimated_credits * effectiveImageCount || 0;
    const totalCost = totalCredits * 0.01; // Assuming $0.01 per credit

    return (
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 dark:from-blue-950/30 dark:to-purple-950/30 dark:border-blue-800">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-5">
            <div className="text-center">
              <div className="font-semibold text-blue-700 dark:text-blue-300">{stats?.estimated_time || 'Unknown'}</div>
              <div className="flex justify-center items-center text-muted-foreground">
                <Clock className="mr-1 w-3 h-3" />
                Processing Time
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-purple-700 dark:text-purple-300">{formatNumber(stats?.estimated_credits || 0)}</div>
              <div className="flex justify-center items-center text-muted-foreground">
                <Zap className="mr-1 w-3 h-3" />
                Credits/Image
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-indigo-700 dark:text-indigo-300">{formatNumber(totalCredits)}</div>
              <div className="flex justify-center items-center text-muted-foreground">
                <Calculator className="mr-1 w-3 h-3" />
                Total Credits
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-green-700 dark:text-green-300">{formatCurrency(totalCost)}</div>
              <div className="flex justify-center items-center text-muted-foreground">
                <DollarSign className="mr-1 w-3 h-3" />
                Total Cost
              </div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-orange-700 dark:text-orange-300">{stats?.ai_features_count || 0}</div>
              <div className="flex justify-center items-center text-muted-foreground">
                <Wand2 className="mr-1 w-3 h-3" />
                AI Features
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Permission Warning */}
      {!hasPermission('jobs', 'create') && (
        <WarningBanner
          variant="error"
          title="View Only Mode"
          message="You don't have permission to create processing jobs. You can view the transformation options but cannot create jobs. Please contact your administrator if you need access."
          icon={<X className="h-5 w-5" />}
        />
      )}

      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl font-semibold">
              Image Transformation Settings
            </CardTitle>
            <div className="flex gap-2 items-center">
              {isAnalyzingTransformations && (
                <Badge variant="outline" className="text-xs">
                  <Loader2 className="mr-1 w-3 h-3 animate-spin" />
                  Analyzing...
                </Badge>
              )}
              {renderProcessingMethodBadge()}
              <Button variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground">
            Configure how your {selectedProductIds.length} selected products ({isLoadingImageCount ? 'calculating...' : `${effectiveImageCount} images`}) will be processed
          </p>
        </CardHeader>
      </Card>

      {/* Image Type Selection */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileImage className="w-4 h-4" />
            Image Selection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Choose which product images to process across all selected products
            </p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => hasPermission('jobs', 'create') && setImageTypeSelection('all')}
                disabled={!hasPermission('jobs', 'create')}
                title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
                className={`p-3 rounded-lg border-2 transition-all ${
                  !hasPermission('jobs', 'create')
                    ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : imageTypeSelection === 'all'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-center space-y-1">
                  <div className="font-medium">All Images</div>
                  <div className="text-xs text-muted-foreground">
                    Process main & gallery images
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => hasPermission('jobs', 'create') && setImageTypeSelection('main')}
                disabled={!hasPermission('jobs', 'create')}
                title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
                className={`p-3 rounded-lg border-2 transition-all ${
                  !hasPermission('jobs', 'create')
                    ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : imageTypeSelection === 'main'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-center space-y-1">
                  <div className="font-medium">Main Only</div>
                  <div className="text-xs text-muted-foreground">
                    Process main images only
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => hasPermission('jobs', 'create') && setImageTypeSelection('gallery')}
                disabled={!hasPermission('jobs', 'create')}
                title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
                className={`p-3 rounded-lg border-2 transition-all ${
                  !hasPermission('jobs', 'create')
                    ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : imageTypeSelection === 'gallery'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-center space-y-1">
                  <div className="font-medium">Gallery Only</div>
                  <div className="text-xs text-muted-foreground">
                    Process gallery images only
                  </div>
                </div>
              </button>
            </div>
            
            {/* Show estimated image count based on selection */}
            <div className="mt-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Estimated images to process:</span>
                <span className="font-medium">
                  {isLoadingImageCount ? (
                    <Loader2 className="w-4 h-4 animate-spin inline" />
                  ) : effectiveImageCount}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Processing Estimate */}
      {renderProcessingEstimate()}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* AI Features Alert */}
      {processingInfo?.processing_info?.method === 'upload' && (
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800">
          <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            <strong>AI Processing Enabled:</strong> Your images will be uploaded to Cloudinary for advanced AI processing. 
            Files are automatically cleaned up after successful processing to minimize storage costs.
          </AlertDescription>
        </Alert>
      )}

      {/* Processing Method Information */}
      {processingInfo && (
        <Alert className="bg-muted/50 border-border">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-foreground">
            <div className="space-y-1">
              <div>
                <strong>Method:</strong> {processingInfo.processing_info?.method === 'upload' ? 'AI Processing' : 'Basic Processing'}
              </div>
              <div>
                <strong>Storage Required:</strong> {processingInfo.processing_info?.storage_required ? 'Temporary (auto-cleanup)' : 'None'}
              </div>
              <div>
                <strong>AI Features:</strong> {processingInfo.stats?.ai_features_count || 0} active
              </div>
              {processingInfo.processing_info?.method === 'upload' && (
                <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                  ℹ️ Images are uploaded for AI processing and automatically cleaned up after successful job completion
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Presets Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex gap-2 items-center">
              <Sparkles className="w-5 h-5" />
              Transformation Presets
            </CardTitle>
            <Badge className="gap-1" variant="outline">
              <Crown className="w-3 h-3" />
              {userPlan} Plan
            </Badge>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Quick-start presets for common image transformations
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* All presets available to all plans */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {Object.entries(presets).map(([key, preset]) => {
              const hasAIFeatures = preset.transformations.some(t => isAITransformation(t.type));

              const usageInfo = getUsageInfo(preset);

              return (
                <div
                  key={key}
                  className={`relative p-4 border-2 rounded-lg transition-all ${
                    hasPermission('jobs', 'create')
                      ? 'cursor-pointer hover:bg-accent/50'
                      : 'opacity-60 cursor-not-allowed'
                  } ${
                    key === 'invisible_mannequin' || key === 'invisible_mannequin_pro'
                      ? 'bg-gradient-to-br from-purple-100 via-pink-50 to-purple-100 border-purple-400 dark:from-purple-900/40 dark:via-pink-900/30 dark:to-purple-900/40 dark:border-purple-500 ring-2 ring-purple-200 dark:ring-purple-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                      : key === 'object_placement' || key === 'object_placement_pro'
                        ? 'bg-gradient-to-br from-amber-100 via-orange-50 to-amber-100 border-amber-400 dark:from-amber-900/40 dark:via-orange-900/30 dark:to-amber-900/40 dark:border-amber-500 ring-2 ring-amber-200 dark:ring-amber-700 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
                        : hasAIFeatures
                          ? 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200 dark:from-blue-950/30 dark:to-purple-950/30 dark:border-blue-800'
                          : 'border-border hover:border-border/80'
                  }`}
                  onClick={() => hasPermission('jobs', 'create') && addPreset(key)}
                  title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
                >
                  {/* Special indicator for Ghost Mannequin */}
                  {(key === 'invisible_mannequin' || key === 'invisible_mannequin_pro') && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="relative">
                        <div className="absolute inset-0 bg-purple-500 rounded-full animate-ping opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-2">
                          <Shirt className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Special indicator for Object Placement */}
                  {(key === 'object_placement' || key === 'object_placement_pro') && (
                    <div className="absolute -top-3 -right-3 z-10">
                      <div className="relative">
                        <div className="absolute inset-0 bg-amber-500 rounded-full animate-ping opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-full p-2">
                          <Home className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Lock overlay for no permission */}
                  {!hasPermission('jobs', 'create') && (
                    <div className="flex absolute inset-0 justify-center items-center rounded-lg backdrop-blur-sm bg-background/80">
                      <div className="space-y-2 text-center">
                        <div className="flex justify-center items-center mx-auto w-12 h-12 rounded-full bg-muted">
                          <Crown className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium">No Permission</p>
                      </div>
                    </div>
                  )}
                        
                        <div className="flex justify-between items-start mb-2">
                          <h4 className={`font-semibold ${
                            key === 'invisible_mannequin' || key === 'invisible_mannequin_pro' ? 'text-purple-900 dark:text-purple-100 text-lg'
                            : key === 'object_placement' || key === 'object_placement_pro' ? 'text-amber-900 dark:text-amber-100 text-lg'
                            : ''
                          }`}>
                            {preset.name}
                            {key === 'invisible_mannequin' && (
                              <span className="ml-2 text-sm font-normal text-purple-600 dark:text-purple-300">
                                (Fashion Industry Standard)
                              </span>
                            )}
                            {key === 'invisible_mannequin_pro' && (
                              <span className="ml-2 text-sm text-purple-700 dark:text-purple-200 font-semibold">
                                (Enhanced AI Processing)
                              </span>
                            )}
                            {key === 'object_placement' && (
                              <span className="ml-2 text-sm font-normal text-amber-600 dark:text-amber-300">
                                (Virtual Staging)
                              </span>
                            )}
                            {key === 'object_placement_pro' && (
                              <span className="ml-2 text-sm text-amber-700 dark:text-amber-200 font-semibold">
                                (Enhanced AI Processing)
                              </span>
                            )}
                          </h4>
                          <div className="flex gap-1">
                            {key === 'invisible_mannequin' && (
                              <Badge className="text-xs text-white bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse">
                                ✨ Premium
                              </Badge>
                            )}
                            {key === 'invisible_mannequin_pro' && (
                              <Badge className="text-xs text-white bg-gradient-to-r from-purple-700 via-pink-600 to-fuchsia-600 animate-pulse shadow-lg">
                                ⭐ Premium Pro
                              </Badge>
                            )}
                            {key === 'object_placement' && (
                              <Badge className="text-xs text-white bg-gradient-to-r from-amber-600 to-orange-600 animate-pulse">
                                🏠 Virtual Staging
                              </Badge>
                            )}
                            {key === 'object_placement_pro' && (
                              <Badge className="text-xs text-white bg-gradient-to-r from-amber-700 via-orange-600 to-yellow-600 animate-pulse shadow-lg">
                                ⭐ Virtual Staging Pro
                              </Badge>
                            )}
                            {preset.estimatedCredits && (
                              <Badge variant={key === 'invisible_mannequin' || key === 'object_placement' || key === 'invisible_mannequin_pro' || key === 'object_placement_pro' ? 'default' : 'outline'} className={`text-xs ${
                                key === 'invisible_mannequin' || key === 'invisible_mannequin_pro' ? 'bg-purple-600 text-white'
                                : key === 'object_placement' || key === 'object_placement_pro' ? 'bg-amber-600 text-white'
                                : ''
                              }`}>
                                {preset.estimatedCredits} credits
                              </Badge>
                            )}
                            {hasAIFeatures && key !== 'invisible_mannequin' && key !== 'object_placement' && key !== 'invisible_mannequin_pro' && key !== 'object_placement_pro' && (
                              <Badge className="text-xs text-white bg-gradient-to-r from-blue-500 to-purple-500">
                                <Sparkles className="mr-1 w-3 h-3" />
                                AI
                              </Badge>
                            )}
                          </div>
                        </div>
                        <p className={`mb-2 text-sm ${
                          key === 'invisible_mannequin' || key === 'invisible_mannequin_pro'
                            ? 'text-purple-700 dark:text-purple-300 font-medium'
                            : key === 'object_placement' || key === 'object_placement_pro'
                              ? 'text-amber-700 dark:text-amber-300 font-medium'
                              : 'text-muted-foreground'
                        }`}>
                          {preset.description}
                          {key === 'invisible_mannequin' && (
                            <span className="block mt-1 text-xs text-purple-600 dark:text-purple-400">
                              🎯 Perfect for fashion e-commerce • Removes models while keeping garment shape
                            </span>
                          )}
                          {key === 'invisible_mannequin_pro' && (
                            <span className="block mt-1 text-xs text-purple-700 dark:text-purple-300 font-semibold">
                              ⭐ Premium AI • 2x Enhanced Quality & Precision
                            </span>
                          )}
                          {key === 'object_placement' && (
                            <span className="block mt-1 text-xs text-amber-600 dark:text-amber-400">
                              🏠 Place products in realistic environments • Perfect for home & furniture
                            </span>
                          )}
                          {key === 'object_placement_pro' && (
                            <span className="block mt-1 text-xs text-amber-700 dark:text-amber-300 font-semibold">
                              ⭐ Premium AI • 2x Ultra-Realistic Environments
                            </span>
                          )}
                        </p>

                        {/* Usage Information for high-cost transformations - but not for mannequin transformations */}
                        {usageInfo && key !== 'invisible_mannequin' && key !== 'invisible_mannequin_pro' && key !== 'object_placement' && key !== 'object_placement_pro' && (
                          <div className="p-2 mb-2 rounded bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                            <div className="flex justify-between items-center text-xs">
                              <span className="font-medium text-amber-800 dark:text-amber-200">
                                Usage Available:
                              </span>
                              <span className="font-bold text-amber-900 dark:text-amber-100">
                                {usageInfo.maxTransformations} images
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                              Based on {usageInfo.remainingCredits} remaining credits (${usageInfo.costPerTransformation} per image)
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap gap-1 mb-2">
                          {preset.transformations.slice(0, 3).map((t, i) => (
                            <Badge
                              key={i}
                              variant="secondary"
                              className={`text-xs ${getTransformationBadgeClass(t.type)}`}
                            >
                              {getTransformationIcon(t.type)}
                              <span className="ml-1">{jobService.formatTransformation(t)}</span>
                              {isAITransformation(t.type) && t.type !== 'invisible_mannequin' && t.type !== 'object_placement' && (
                                <span className="ml-1 text-[10px] font-semibold">AI</span>
                              )}
                            </Badge>
                          ))}
                          {preset.transformations.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{preset.transformations.length - 3} more
                            </Badge>
                          )}
                        </div>
                        {preset.processingTime && (
                          <div className="flex gap-1 items-center text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {preset.processingTime}
                          </div>
                        )}
              </div>
            );
          })}
          </div>
        </CardContent>
      </Card>

      {/* Custom Transformations */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex gap-2 items-center">
              <Settings className="w-5 h-5" />
              Custom Transformations
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCustomForm(!showCustomForm)}
              disabled={!hasPermission('jobs', 'create')}
              title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
            >
              <Plus className="mr-1 w-4 h-4" />
              {!hasPermission('jobs', 'create') ? 'No Permission' : 'Add Custom'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Transformations */}
          {transformations.length > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Selected Transformations:</h4>
                <div className="flex gap-3 items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={autoSortTransformations}
                    disabled={!hasPermission('jobs', 'create')}
                    className="h-7 text-xs"
                    title={!hasPermission('jobs', 'create') ? "You don't have permission to modify jobs" : undefined}
                  >
                    <Settings className="mr-1 w-3 h-3" />
                    Auto Sort
                  </Button>
                  <div className="flex gap-2 items-center text-xs text-muted-foreground">
                    <ArrowDown className="w-3 h-3" />
                    <span>Processing Order</span>
                  </div>
                </div>
              </div>
              
              {/* Order Status Alert */}
              {!isOptimalOrder() ? (
                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
                  <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  <AlertDescription className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>⚠️ Suboptimal order detected!</strong> For best results, AI effects should come before resizing and format conversion. 
                    <Button 
                      variant="link" 
                      size="sm" 
                      onClick={autoSortTransformations}
                      disabled={!hasPermission('jobs', 'create')}
                      className="p-0 ml-1 h-auto text-amber-700 underline dark:text-amber-300"
                      title={!hasPermission('jobs', 'create') ? "You don't have permission to modify jobs" : undefined}
                    >
                      Click to auto-sort
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert className="bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                    <strong>✅ Optimal order!</strong> Your transformations are arranged for best processing results.
                  </AlertDescription>
                </Alert>
              )}
              
              {transformations.map((transformation, index) => {
                const isAI = isAITransformation(transformation.type);

                return (
                  <div
                    key={index}
                    className={`flex justify-between items-center p-3 rounded-lg border-l-4 ${
                      isAI
                        ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-500'
                        : 'bg-accent/30 border-primary/30'
                    }`}
                  >
                    <div className="flex gap-3 items-center">
                      <div className="flex justify-center items-center w-6 h-6 text-xs font-medium rounded-full bg-primary/10 text-primary">
                        {index + 1}
                      </div>
                      {getTransformationIcon(transformation.type)}
                      <span>{jobService.formatTransformation(transformation)}</span>
                      {isAI && (
                        <Badge className="text-xs text-white bg-gradient-to-r from-blue-500 to-purple-500">
                          <Sparkles className="mr-1 w-3 h-3" />
                          AI
                        </Badge>
                      )}
                    </div>
                  <div className="flex gap-2 items-center">
                    {/* Move Up Button */}
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveTransformation(index, index - 1)}
                        disabled={!hasPermission('jobs', 'create')}
                        title={!hasPermission('jobs', 'create') ? "You don't have permission to modify jobs" : "Move up"}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Move Down Button */}
                    {index < transformations.length - 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveTransformation(index, index + 1)}
                        disabled={!hasPermission('jobs', 'create')}
                        title={!hasPermission('jobs', 'create') ? "You don't have permission to modify jobs" : "Move down"}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTransformation(index)}
                      disabled={!hasPermission('jobs', 'create')}
                      title={!hasPermission('jobs', 'create') ? "You don't have permission to modify jobs" : "Remove transformation"}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
            </div>
          )}

          {/* Custom Transformation Form */}
          {showCustomForm && (
            <div className="p-6 space-y-6 rounded-lg border-2 border-dashed border-primary/20 bg-accent/30">
              <h4 className="flex gap-2 items-center text-lg font-semibold">
                <Plus className="w-5 h-5 text-primary" />
                Add Custom Transformation
              </h4>
              
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Transformation Type</label>
                  <Select
                    value={customTransformation.type || 'none'}
                    onValueChange={(value) => setCustomTransformation({
                      ...customTransformation,
                      type: value === 'none' ? undefined : value as Transformation['type']
                    })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select transformation type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select type...</SelectItem>

                      {/* Basic Transformations */}
                      <SelectItem value="resize">📐 Resize & Crop</SelectItem>
                      <SelectItem value="format_conversion">🖼️ Format Conversion</SelectItem>
                      <SelectItem value="color_adjustment">🌈 Color Adjustment</SelectItem>
                      <SelectItem value="sharpen">🔍 Sharpen</SelectItem>
                      <SelectItem value="blur">💫 Blur</SelectItem>

                      {/* AI Features */}
                      <SelectItem value="background_removal">🎨 AI Background Removal</SelectItem>
                      <SelectItem value="ai_upscale">⚡ AI Upscaling (2x, 4x)</SelectItem>
                      <SelectItem value="generative_remove">👁️ AI Generative Remove</SelectItem>
                      <SelectItem value="generative_fill">🪄 AI Generative Fill</SelectItem>
                      <SelectItem value="quality_enhancement">✨ AI Quality Enhancement</SelectItem>
                      <SelectItem value="auto_enhancement">🔧 AI Auto Enhancement</SelectItem>
                      <SelectItem value="noise_reduction">🔇 AI Noise Reduction</SelectItem>

                      {/* Advanced AI Features */}
                      <SelectItem value="generative_replace">🧠 AI Generative Replace</SelectItem>
                      <SelectItem value="generative_background_replace">🌟 AI Generative Background Replace</SelectItem>
                      <SelectItem value="auto_tagging">🏷️ AI Auto Tagging</SelectItem>
                      <SelectItem value="object_detection">👁️‍🗨️ AI Object Detection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Type-specific fields */}
                {customTransformation.type === 'background_removal' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Background Color (optional)</label>
                    <Select
                      value={customTransformation.backgroundColor || 'transparent'}
                      onValueChange={(value) => setCustomTransformation({
                        ...customTransformation,
                        backgroundColor: value
                      })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select background color" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="transparent">Transparent</SelectItem>
                        <SelectItem value="white">White</SelectItem>
                        <SelectItem value="black">Black</SelectItem>
                        <SelectItem value="lightgray">Light Gray</SelectItem>
                        <SelectItem value="lightblue">Light Blue</SelectItem>
                        <SelectItem value="lightgreen">Light Green</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {customTransformation.type === 'generative_fill' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Target Width (px)</label>
                      <Input
                        type="number"
                        placeholder="1200"
                        value={customTransformation.width || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          width: parseInt(e.target.value) || undefined
                        })}
                        min={100}
                        max={4000}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Target Height (px)</label>
                      <Input
                        type="number"
                        placeholder="1200"
                        value={customTransformation.height || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          height: parseInt(e.target.value) || undefined
                        })}
                        min={100}
                        max={4000}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Fill Style</label>
                      <Select
                        value={customTransformation.prompt || 'auto'}
                        onValueChange={(value) => setCustomTransformation({
                          ...customTransformation,
                          prompt: value
                        })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select fill style" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto (Natural extension)</SelectItem>
                          <SelectItem value="product_showcase">Product Showcase (Clean studio)</SelectItem>
                          <SelectItem value="lifestyle">Lifestyle (Natural environment)</SelectItem>
                          <SelectItem value="minimal">Minimal (Simple background)</SelectItem>
                          <SelectItem value="seamless">Seamless Pattern</SelectItem>
                          <SelectItem value="gradient">Gradient Background</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {customTransformation.type === 'generative_remove' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Objects to Remove</label>
                    <Textarea
                      placeholder="Describe what to remove (e.g., 'remove the background objects', 'remove the watermark')..."
                      value={customTransformation.prompt || ''}
                      onChange={(e) => setCustomTransformation({
                        ...customTransformation,
                        prompt: e.target.value
                      })}
                      maxLength={500}
                      required
                    />
                  </div>
                )}

                {customTransformation.type === 'generative_replace' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Replace This</label>
                      <Input
                        placeholder="Object to replace (e.g., 'the red shirt')"
                        value={customTransformation.from || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          from: e.target.value
                        })}
                        maxLength={200}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">With This</label>
                      <Input
                        placeholder="Replacement description (e.g., 'a blue shirt')"
                        value={customTransformation.to || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          to: e.target.value
                        })}
                        maxLength={200}
                        required
                      />
                    </div>
                  </>
                )}

                {customTransformation.type === 'generative_background_replace' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Background Description</label>
                    <Textarea
                      placeholder="Describe the new background (e.g., 'professional studio background', 'outdoor nature scene', 'modern office setting')..."
                      value={customTransformation.prompt || ''}
                      onChange={(e) => setCustomTransformation({
                        ...customTransformation,
                        prompt: e.target.value
                      })}
                      maxLength={500}
                      required
                    />
                  </div>
                )}

                {customTransformation.type === 'auto_tagging' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Confidence Level</label>
                    <Select
                      value={customTransformation.confidence?.toString() || '0.7'}
                      onValueChange={(value) => setCustomTransformation({
                        ...customTransformation,
                        confidence: parseFloat(value)
                      })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select confidence level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.5">50% - More tags, less accuracy</SelectItem>
                        <SelectItem value="0.7">70% - Balanced (recommended)</SelectItem>
                        <SelectItem value="0.8">80% - Fewer tags, higher accuracy</SelectItem>
                        <SelectItem value="0.9">90% - Very selective</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {customTransformation.type === 'object_detection' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Objects to Detect</label>
                    <Input
                      placeholder="Specific objects (e.g., 'person', 'car', 'product') or leave empty for all"
                      value={customTransformation.objects || ''}
                      onChange={(e) => setCustomTransformation({
                        ...customTransformation,
                        objects: e.target.value
                      })}
                    />
                  </div>
                )}

                {customTransformation.type === 'resize' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Width (px)</label>
                      <Input
                        type="number"
                        placeholder="Enter width"
                        value={customTransformation.width || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          width: parseInt(e.target.value) || undefined
                        })}
                        max={4000}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Height (px)</label>
                      <Input
                        type="number"
                        placeholder="Enter height"
                        value={customTransformation.height || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          height: parseInt(e.target.value) || undefined
                        })}
                        max={4000}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Crop Mode</label>
                      <Select
                        value={customTransformation.mode || 'pad'}
                        onValueChange={(value) => setCustomTransformation({
                          ...customTransformation,
                          mode: value as 'fit' | 'pad' | 'mfit' | 'fill' | 'scale'
                        })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select crop mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pad">Pad - Exact dimensions with padding (recommended)</SelectItem>
                          <SelectItem value="fit">Fit - Contains entire image (may be smaller)</SelectItem>
                          <SelectItem value="fill">Fill - Fills dimensions, may crop</SelectItem>
                          <SelectItem value="crop">Crop - Extract region</SelectItem>
                          <SelectItem value="scale">Scale - Exact dimensions</SelectItem>
                          <SelectItem value="limit">Limit - Only scale down</SelectItem>
                          <SelectItem value="mfit">MFit - Scale up to fit</SelectItem>
                          <SelectItem value="thumb">Thumb - Smart thumbnail</SelectItem>
                          <SelectItem value="auto">Auto - AI determines best</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Gravity for fill, crop, thumb modes */}
                    {['fill', 'crop', 'thumb'].includes(customTransformation.mode || '') && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Focus Area (Gravity)</label>
                        <Select
                          value={customTransformation.gravity || 'auto'}
                          onValueChange={(value) => setCustomTransformation({
                            ...customTransformation,
                            gravity: value as Transformation['gravity']
                          })}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select focus area" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto - AI detection</SelectItem>
                            <SelectItem value="face">Face - Largest face</SelectItem>
                            <SelectItem value="faces">Faces - All faces</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="north">Top</SelectItem>
                            <SelectItem value="south">Bottom</SelectItem>
                            <SelectItem value="east">Right</SelectItem>
                            <SelectItem value="west">Left</SelectItem>
                            <SelectItem value="north_east">Top-Right</SelectItem>
                            <SelectItem value="north_west">Top-Left</SelectItem>
                            <SelectItem value="south_east">Bottom-Right</SelectItem>
                            <SelectItem value="south_west">Bottom-Left</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </>
                )}

                {customTransformation.type === 'format_conversion' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Format</label>
                      <Select
                        value={customTransformation.format || 'none'}
                        onValueChange={(value) => setCustomTransformation({
                          ...customTransformation,
                          format: value === 'none' ? undefined : value as 'jpg' | 'png' | 'webp' | 'avif'
                        })}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select output format" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto - Best format</SelectItem>
                          <SelectItem value="jpg">JPG - Smaller files</SelectItem>
                          <SelectItem value="png">PNG - Transparency</SelectItem>
                          <SelectItem value="webp">WebP - Modern format</SelectItem>
                          <SelectItem value="avif">AVIF - Next-gen format</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Quality (%)</label>
                      <Input
                        type="number"
                        placeholder="85"
                        value={customTransformation.quality || 85}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          quality: parseInt(e.target.value) || 85
                        })}
                        min={1}
                        max={100}
                      />
                    </div>
                  </>
                )}

                {customTransformation.type === 'ai_upscale' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Scale Factor</label>
                    <Select
                      value={customTransformation.scale?.toString() || '2'}
                      onValueChange={(value) => setCustomTransformation({
                        ...customTransformation,
                        scale: parseInt(value) as 2 | 4
                      })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select scale factor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2x Upscale - Double size</SelectItem>
                        <SelectItem value="4">4x Upscale - Quadruple size</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {customTransformation.type === 'color_adjustment' && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Brightness (-99 to 100)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={customTransformation.brightness || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          brightness: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        min={-99}
                        max={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Saturation (-100 to 100)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={customTransformation.saturation || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          saturation: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        min={-100}
                        max={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Contrast (-100 to 100)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={customTransformation.contrast || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          contrast: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        min={-100}
                        max={100}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Hue (-100 to 100)</label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={customTransformation.hue || ''}
                        onChange={(e) => setCustomTransformation({
                          ...customTransformation,
                          hue: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        min={-100}
                        max={100}
                      />
                    </div>
                  </>
                )}

                {(customTransformation.type === 'sharpen' || customTransformation.type === 'blur') && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      {customTransformation.type === 'sharpen' ? 'Sharpening' : 'Blur'} Strength (1-2000)
                    </label>
                    <Input
                      type="number"
                      placeholder={customTransformation.type === 'sharpen' ? '400' : '300'}
                      value={customTransformation.strength || ''}
                      onChange={(e) => setCustomTransformation({
                        ...customTransformation,
                        strength: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      min={1}
                      max={2000}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <Button 
                  onClick={addCustomTransformation} 
                  className="gap-2 min-w-[140px] h-10"
                  disabled={!hasPermission('jobs', 'create')}
                  title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
                >
                  <CheckCircle className="w-4 h-4" />
                  {!hasPermission('jobs', 'create') ? 'No Permission' : 'Add Transformation'}
                </Button>
                <Button variant="outline" onClick={() => setShowCustomForm(false)} className="gap-2 min-w-[100px] h-10">
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Credit Limit Warning */}
      {creditCheck.isOverLimit && creditCheck.details && (
        <CreditLimitBanner
          required={creditCheck.details.required}
          remaining={creditCheck.details.remaining}
          current={creditCheck.details.current}
          limit={creditCheck.details.limit}
          planName={creditCheck.details.planName}
          products={creditCheck.details.products}
          images={creditCheck.details.images}
          creditsPerImage={creditCheck.details.creditsPerImage}
          onUpgrade={() => {
            // Navigate to billing page or open upgrade modal
            window.open('/billing', '_blank');
          }}
        />
      )}

      {/* Cost Estimation & Action */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <div className="flex gap-2 items-center">
                <Calculator className="w-4 h-4" />
                <span className="font-medium">Estimated Processing Cost</span>
              </div>
              <p className="text-2xl font-bold text-primary">{formatCredits(estimatedCost)}</p>
              <p className="text-sm text-muted-foreground">
                Based on {effectiveImageCount} images and {transformations.length} transformation{transformations.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <Button
              onClick={createJobs}
              disabled={!hasPermission('jobs', 'create') || transformations.length === 0 || isCreatingJobs || creditCheck.isOverLimit || isLoadingSubscription}
              className="min-w-[140px]"
              title={!hasPermission('jobs', 'create') ? "You don't have permission to create jobs" : undefined}
            >
              {!hasPermission('jobs', 'create') ? (
                <>
                  <X className="mr-2 w-4 h-4" />
                  No Permission
                </>
              ) : isCreatingJobs ? (
                <>
                  <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                  Creating Jobs...
                </>
              ) : creditCheck.isOverLimit ? (
                <>
                  <DollarSign className="mr-2 w-4 h-4" />
                  Upgrade Required
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 w-4 h-4" />
                  Start Processing
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}